import cn from 'classnames'
import { observer } from 'mobx-react'
import React, { useRef, useState } from 'react'

import {
   Button,
   Hierarchy,
   IconButton,
   IconButtonHierarchy,
   IconButtonSubVariant,
   SubVariant,
   TextArea
} from '@flowwai-frontend/design-system'
import {
   FlipBackward01Icon,
   Send03OutlineIcon,
   Star06Icon
} from '@flowwai-frontend/design-system/icons'
import { useCustomT } from '@flowwai-frontend/shared/common-components'
import { ENTER_KEY_VALUE } from '@flowwai-frontend/shared/constants'
import { openLinkInNewTab } from '@flowwai-frontend/shared/utils/common'

import {
   APP_PRIVACY_STATEMENT_LINK,
   APP_TERMS_OF_USE_LINK
} from '../../constants/ChatBoxConstants'
import { ChatBoxTextFieldType } from '../../types/ChatBoxTypes'
import { theme } from '../../theme'

import * as Styles from './styles'

interface Props extends ChatBoxTextFieldType {
   containerClassName?: string
   isDisabled?: boolean
}

const ChatBoxTextField = (props: Props): React.ReactElement => {
   const {
      onChangeValue,
      onClickSend,

      chatBoxTextDescription,
      chatBoxVariant = 'DEFAULT',
      containerClassName,
      shouldResetValueOnSend = true,
      textValue,
      isDisabled
   } = props

   const userTextQueryRef = useRef<string>(textValue ?? '')

   const [isFocus, setIsFocus] = useState<boolean>(!!props.autoFocus)

   const [userTextQuery, setUserTextQuery] = useState<string | undefined>(
      textValue
   )

   const trimmedUserTextQuery = userTextQuery?.trim()

   const t = useCustomT('aiChatSupport:chatBox.chatBoxTextField')

   const chatBoxTheme = theme.chatBox.chatBoxTextField

   const isQueryUpdated =
      trimmedUserTextQuery !== userTextQueryRef.current.trim()

   const isSendButtonDisabled =
      props.isActionButtonDisabled || !trimmedUserTextQuery || isDisabled

   const moduleName = t('aiChatSupport')

   const shouldShowRestoreIcon =
      props.shouldShowRestoreIcon &&
      isQueryUpdated &&
      !props.isChatBoxTextDisabled

   const onClickPrivacyStatement = (): void => {
      openLinkInNewTab(APP_PRIVACY_STATEMENT_LINK)
   }

   const renderPrivacyStatementText = (): React.ReactElement => (
      <span
         className={cn(
            Styles.linkTextStyles,
            chatBoxTheme.descriptionTextColor,
            chatBoxTheme.descriptionLinkTextUnderLineColor
         )}
         onClick={onClickPrivacyStatement}
      >
         {t('privacyStatementText')}
      </span>
   )

   const onClickTermsOfUse = (): void => {
      openLinkInNewTab(APP_TERMS_OF_USE_LINK)
   }

   const renderTermsOfUseText = (): React.ReactElement => (
      <span
         className={cn(
            Styles.linkTextStyles,
            chatBoxTheme.descriptionTextColor,
            chatBoxTheme.descriptionLinkTextUnderLineColor
         )}
         onClick={onClickTermsOfUse}
      >
         {t('termsOfUseText')}
      </span>
   )

   const renderDescriptionWithTermsAndPrivacy = (): React.ReactElement => (
      <span
         className={cn(
            Styles.commonDescriptionTextStyles,
            chatBoxTheme.descriptionTextColor
         )}
      >
         {t('byInteractingText', { moduleName })}
         {renderTermsOfUseText()}
         {t('confirmYouReviewedText')}
         {renderPrivacyStatementText()}
      </span>
   )

   const renderDescriptionText = (): React.ReactElement =>
      chatBoxTextDescription ? (
         <span
            className={cn(
               Styles.commonDescriptionTextStyles,
               chatBoxTheme.descriptionTextColor
            )}
         >
            {chatBoxTextDescription}
         </span>
      ) : (
         renderDescriptionWithTermsAndPrivacy()
      )

   const onFocusTextArea = () => {
      setIsFocus(true)
   }

   const renderSendIcon = (stroke: string): React.ReactElement => (
      <Send03OutlineIcon
         height={20}
         width={20}
         stroke={isSendButtonDisabled ? stroke : chatBoxTheme.sendIconColor}
      />
   )

   const renderSendButton = (): React.ReactElement => (
      <IconButton
         hierarchy={IconButtonHierarchy.Secondary}
         subVariant={IconButtonSubVariant.GrayOutline}
         size={'Small'}
         icon={({ stroke }) => renderSendIcon(stroke)}
         isDisabled={isSendButtonDisabled}
         onClick={onClickActionButton}
      />
   )

   const renderGenerateButtonLeftIcon = (
      stroke: string
   ): React.ReactElement => <Star06Icon height={20} width={20} fill={stroke} />

   const renderGenerateButton = (): React.ReactElement => (
      <Button
         hierarchy={Hierarchy.Primary}
         size={'Small'}
         subVariant={SubVariant.Primary}
         leftIcon={({ stroke }) => renderGenerateButtonLeftIcon(stroke)}
         isDisabled={isSendButtonDisabled}
         onClick={onClickActionButton}
      >
         {t('generate')}
      </Button>
   )

   const onClickRemove = () => {
      setUserTextQuery(userTextQueryRef.current)
      onFocusTextArea()
   }

   const renderRestoreIcon = (stroke: string): React.ReactElement => (
      <FlipBackward01Icon height={24} width={24} fill={stroke} />
   )

   const renderRestoreButton = (): React.ReactElement => (
      <IconButton
         hierarchy={IconButtonHierarchy.Tertiary}
         size={'Small'}
         subVariant={IconButtonSubVariant.Gray}
         icon={({ stroke }) => renderRestoreIcon(stroke)}
         onClick={onClickRemove}
      />
   )

   const renderChatBoxActionBasedOnVariant = (): React.ReactElement => {
      switch (chatBoxVariant) {
         case 'GENERATE': {
            return (
               <>
                  {shouldShowRestoreIcon ? renderRestoreButton() : null}
                  {renderGenerateButton()}
               </>
            )
         }

         case 'DEFAULT':
         default: {
            return renderSendButton()
         }
      }
   }

   const onClickActionButton = (): void => {
      if (!trimmedUserTextQuery) return

      userTextQueryRef.current = trimmedUserTextQuery

      setIsFocus(false)

      onClickSend(trimmedUserTextQuery)

      if (shouldResetValueOnSend) setUserTextQuery('')
      else setUserTextQuery(trimmedUserTextQuery)
   }

   const onKeyDown = (e: React.KeyboardEvent): void => {
      const isEnterKey = e.key === ENTER_KEY_VALUE

      const isShiftAndEnterKey = e.shiftKey && isEnterKey

      if (isShiftAndEnterKey) return

      if (isEnterKey) {
         e.preventDefault()
         if (!isSendButtonDisabled) onClickActionButton()
      }
   }

   const onChangeUserTextQuery = (value: string): void => {
      setUserTextQuery(value)

      onChangeValue?.(value)
   }

   const renderChatTextField = (): React.ReactElement => (
      <TextArea
         //NOTE: Label is not Required for ChatBox
         label={''}
         autoFocus={isFocus}
         placeholder={t('placeholderText')}
         value={userTextQuery}
         size={'ExtraSmall'}
         isDisabled={props.isChatBoxTextDisabled}
         onChange={onChangeUserTextQuery}
         onFocus={onFocusTextArea}
         onKeyDown={onKeyDown}
         isReadOnly={isDisabled}
         inputClassName='!max-h-[100px] scrollbar resize-none'
      />
   )

   const renderChatBoxTextFieldWithSendButton = (): React.ReactElement => (
      <div className='w-full flex items-end gap-x-[10px]'>
         {renderChatTextField()}
         {renderChatBoxActionBasedOnVariant()}
      </div>
   )

   return (
      <div className={cn(Styles.containerStyles, containerClassName)}>
         {renderChatBoxTextFieldWithSendButton()}
         {renderDescriptionText()}
      </div>
   )
}

export default observer(ChatBoxTextField)
